<script lang="ts">
  import { api } from '$shared/api';
  import { markAllRead, markRead, fetchUnreadCount } from '$lib/stores/notifications.svelte';
  import { navigate } from '$lib/router.svelte';
  import { notifMessage, notifLink, notifIcon, formatNotifDate, type NotifItem } from '$lib/utils/notifications';
  import Avatar from '$shared/Avatar.svelte';
  import LoadingSkeleton from '$shared/LoadingSkeleton.svelte';

  let notifications = $state<NotifItem[]>([]);
  let loading = $state(true);
  let page = $state(1);
  let hasMore = $state(false);
  let loadingMore = $state(false);

  const PER_PAGE = 20;

  $effect(() => {
    loadNotifications();
  });

  async function loadNotifications() {
    loading = true;
    try {
      const data = await api.get<NotifItem[]>(`/notifications/grouped?per-page=${PER_PAGE}&page=1`);
      notifications = Array.isArray(data) ? data : [];
      hasMore = notifications.length >= PER_PAGE;
      page = 1;
    } catch {
      notifications = [];
    }
    loading = false;
  }

  async function loadMore() {
    loadingMore = true;
    const nextPage = page + 1;
    try {
      const data = await api.get<NotifItem[]>(`/notifications/grouped?per-page=${PER_PAGE}&page=${nextPage}`);
      const more = Array.isArray(data) ? data : [];
      notifications = [...notifications, ...more];
      hasMore = more.length >= PER_PAGE;
      page = nextPage;
    } catch {
      hasMore = false;
    }
    loadingMore = false;
  }

  async function handleMarkAllRead() {
    await markAllRead();
    notifications = notifications.map((n) => ({ ...n, is_read: true }));
  }

  async function handleMarkRead(notif: NotifItem) {
    if (!notif.is_read) {
      await markRead(notif.id);
      const idx = notifications.findIndex((n) => n.id === notif.id);
      if (idx >= 0) notifications[idx] = { ...notifications[idx], is_read: true };
    }
    const link = notifLink(notif);
    if (link) navigate(link);
  }

  const unreadCount = $derived(notifications.filter((n) => !n.is_read).length);
</script>

<div class="narrow">
  <div class="page-header">
    <h1>Notifications</h1>
    {#if unreadCount > 0}
      <button class="btn-mark-all" onclick={handleMarkAllRead}>Mark all as read</button>
    {/if}
  </div>

  <a href="#/account/notifications" class="settings-link">Notification Settings</a>

  {#if loading}
    <LoadingSkeleton lines={8} />
  {:else if notifications.length === 0}
    <div class="empty-state">
      <div class="empty-icon">🔔</div>
      <h2>No notifications yet</h2>
      <p>When someone votes on your track, follows you, or a duel ends, you'll see it here.</p>
    </div>
  {:else}
    <div class="notif-list">
      {#each notifications as notif}
        <button class="notif-row" class:notif-row--unread={!notif.is_read} onclick={() => handleMarkRead(notif)}>
          {#if notif.grouped && notif.actors && notif.actors.length > 1}
            <div class="notif-avatars">
              {#each notif.actors.slice(0, 3) as actor}
                <Avatar username={actor.username} avatarUrl={actor.avatar_url} size="sm" />
              {/each}
            </div>
          {:else if notif.actor}
            <Avatar username={notif.actor.username} avatarUrl={notif.actor.avatar_url} size="sm" />
          {:else}
            <span class="notif-icon">{notifIcon(notif.type)}</span>
          {/if}
          <div class="notif-content">
            <span class="notif-msg">{notifMessage(notif)}</span>
            <span class="notif-time">{formatNotifDate(notif.created_at)}</span>
          </div>
          {#if !notif.is_read}
            <span class="notif-dot"></span>
          {/if}
        </button>
      {/each}
    </div>

    {#if hasMore}
      <div class="load-more">
        <button class="btn btn-outline" onclick={loadMore} disabled={loadingMore}>
          {loadingMore ? 'Loading...' : 'Load more'}
        </button>
      </div>
    {/if}
  {/if}
</div>

<style>
  .narrow {
    max-width: 40rem;
    margin: 0 auto;
  }

  .page-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0.5rem;
  }
  .page-header h1 {
    margin: 0;
  }

  .btn-mark-all {
    font-size: 0.8125rem;
    color: var(--cyan);
    background: none;
    border: none;
    cursor: pointer;
    font-weight: 600;
    padding: 0.25rem 0.5rem;
  }
  .btn-mark-all:hover {
    text-decoration: underline;
  }

  .settings-link {
    display: inline-block;
    font-size: 0.8125rem;
    color: var(--text-muted);
    margin-bottom: 1.5rem;
  }
  .settings-link:hover {
    color: var(--cyan);
  }

  .notif-list {
    display: flex;
    flex-direction: column;
  }

  .notif-row {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    width: 100%;
    padding: 0.75rem 0.75rem;
    background: none;
    border: none;
    border-bottom: 1px solid var(--border);
    cursor: pointer;
    text-align: left;
    color: var(--text);
    transition: background 0.15s;
  }
  .notif-row:last-child {
    border-bottom: none;
  }
  .notif-row:hover {
    background: var(--surface);
  }
  .notif-row--unread {
    background: rgba(0, 210, 211, 0.04);
  }
  .notif-row--unread:hover {
    background: rgba(0, 210, 211, 0.08);
  }

  .notif-icon {
    font-size: 1.25rem;
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    background: var(--surface-2);
    border-radius: 50%;
  }

  .notif-avatars {
    display: flex;
    position: relative;
    width: fit-content;
    flex-shrink: 0;
  }
  .notif-avatars :global(.avatar) {
    width: 32px;
    height: 32px;
    border: 2px solid var(--bg);
    margin-left: -10px;
  }
  .notif-avatars :global(.avatar:first-child) {
    margin-left: 0;
  }

  .notif-content {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
  }
  .notif-msg {
    font-size: 0.875rem;
    line-height: 1.4;
  }
  .notif-time {
    font-size: 0.75rem;
    color: var(--text-muted);
  }

  .notif-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--cyan);
    flex-shrink: 0;
  }

  .load-more {
    text-align: center;
    padding: 1.5rem 0;
  }
</style>
